import { useEffect } from 'react'
import mapboxgl, { Map } from 'mapbox-gl'
import * as turf from '@turf/turf'
import type { ActiveTool, GeoreferenceValue } from './georeference-step'

const PREVIEW_SOURCE_ID = 'documentsPolygonPreviewSource'
const PREVIEW_FILL_LAYER_ID = 'documentsPolygonPreviewFillLayer'
const PREVIEW_LINE_LAYER_ID = 'documentsPolygonPreviewLineLayer'

// Static preview of a saved polygon (Edit flow) — Clear + redraw, no vertex dragging.
export function usePolygonPreview(
  map: Map | null,
  activeTool: ActiveTool,
  georeference: GeoreferenceValue,
) {
  useEffect(() => {
    if (!map) return
    const showPreview =
      activeTool === 'draw' &&
      georeference.type === 'POLYGON' &&
      georeference.geometry?.type === 'Polygon'

    if (showPreview && georeference.geometry?.type === 'Polygon') {
      const data: GeoJSON.Feature<GeoJSON.Polygon> = {
        type: 'Feature',
        properties: {},
        geometry: georeference.geometry,
      }
      const source = map.getSource(PREVIEW_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      if (source) {
        source.setData(data)
      } else {
        map.addSource(PREVIEW_SOURCE_ID, { type: 'geojson', data })
        map.addLayer({
          id: PREVIEW_FILL_LAYER_ID,
          type: 'fill',
          source: PREVIEW_SOURCE_ID,
          paint: { 'fill-color': '#0B2948', 'fill-opacity': 0.1 },
        })
        map.addLayer({
          id: PREVIEW_LINE_LAYER_ID,
          type: 'line',
          source: PREVIEW_SOURCE_ID,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#0B2948', 'line-width': 2.5 },
        })
      }
      const [minLng, minLat, maxLng, maxLat] = turf.bbox(data)
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 0 },
      )
    } else {
      if (map.getLayer(PREVIEW_FILL_LAYER_ID)) map.removeLayer(PREVIEW_FILL_LAYER_ID)
      if (map.getLayer(PREVIEW_LINE_LAYER_ID)) map.removeLayer(PREVIEW_LINE_LAYER_ID)
      if (map.getSource(PREVIEW_SOURCE_ID)) map.removeSource(PREVIEW_SOURCE_ID)
    }

    return () => {
      if (map.getLayer(PREVIEW_FILL_LAYER_ID)) map.removeLayer(PREVIEW_FILL_LAYER_ID)
      if (map.getLayer(PREVIEW_LINE_LAYER_ID)) map.removeLayer(PREVIEW_LINE_LAYER_ID)
      if (map.getSource(PREVIEW_SOURCE_ID)) map.removeSource(PREVIEW_SOURCE_ID)
    }
  }, [map, activeTool, georeference])
}
